// Opts out of experimental React Compiler lint rules. The classic
// useEffect + setState fetch pattern is intentional here.
import { useState, useEffect, useCallback } from 'react';
import { ChevronDown, ChevronRight, History } from 'lucide-react';
import { formatDate } from '../../utils/locale';
import api from '../../api';
import { logger } from '../../utils/logger';

/**
 * EntityActivityLog — collapsible "History" panel for any entity.
 *
 * Fetches `GET /api/audit-log/entity/{entityType}/{entityId}` on first
 * expand (lazy). Newest-first. Each row shows action name + relative
 * time + optional metadata snippet.
 *
 * Props:
 *   - entityType  string  e.g. 'task' | 'call' | 'payment' | 'reading'
 *   - entityId    string  the entity ID
 *   - limit       number  default 25
 *   - defaultOpen boolean default false
 */
const ACTION_LABEL = {
  TASK_CREATE: 'Created',
  TASK_UPDATE: 'Updated',
  TASK_DELETE: 'Deleted',
  TASK_COMPLETE: 'Completed',
  TASK_DEFER: 'Deferred',
  TASK_ARCHIVE: 'Archived',
  TASK_DELEGATE: 'Delegated',
  TASK_TIMER_START: 'Timer started',
  TASK_TIMER_STOP: 'Timer stopped',
  CALL_CREATE: 'Call logged',
  CALL_UPDATE: 'Call updated',
  CALL_COMPLETE: 'Call completed',
  PAYMENT_CREATE: 'Payment created',
  PAYMENT_UPDATE: 'Payment updated',
  PAYMENT_PAID: 'Marked paid',
  READING_CREATE: 'Reading added',
  READING_UPDATE: 'Reading updated',
  READING_READ: 'Marked read',
};

function relativeTime(iso) {
  if (!iso) return '';
  const diff = Date.now() - new Date(iso).getTime();
  const min = Math.floor(diff / 60000);
  if (min < 1) return 'just now';
  if (min < 60) return `${min}m ago`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr}h ago`;
  const d = Math.floor(hr / 24);
  if (d < 7) return `${d}d ago`;
  return formatDate(iso);
}

export default function EntityActivityLog({ entityType, entityId, limit = 25 }) {
  const [open, setOpen] = useState(false);
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loaded, setLoaded] = useState(false);

  const fetchLogs = useCallback(async () => {
    if (!entityType || !entityId) return;
    setLoading(true);
    try {
      const res = await api.get(`/audit-log/entity/${entityType}/${entityId}`, { params: { limit } });
      setLogs(Array.isArray(res.data) ? res.data : []);
      setLoaded(true);
    } catch (err) {
      logger.error('EntityActivityLog fetch failed:', err);
    } finally {
      setLoading(false);
    }
  }, [entityType, entityId, limit]);

  const toggleOpen = useCallback(() => {
    setOpen((prev) => {
      const next = !prev;
      if (next && !loaded) fetchLogs();
      return next;
    });
  }, [loaded, fetchLogs]);

  // Refetch on mutation broadcasts so the trail stays live.
  // Debounced 150ms to avoid hammering the API on bulk ops.
  useEffect(() => {
    let timeoutId = null;
    const handler = () => {
      if (!open) return;
      if (timeoutId) clearTimeout(timeoutId);
      timeoutId = setTimeout(() => { fetchLogs(); }, 150);
    };
    window.addEventListener('polkdesq:data-mutated', handler);
    return () => {
      window.removeEventListener('polkdesq:data-mutated', handler);
      if (timeoutId) clearTimeout(timeoutId);
    };
  }, [open, fetchLogs]);

  if (!entityType || !entityId) return null;

  return (
    <div
      data-testid="entity-activity-log"
      className="rounded-[14px] overflow-hidden"
      style={{ background: 'var(--pd-panel-2)', border: '1px solid var(--pd-border)' }}
    >
      <button
        type="button"
        data-testid="entity-activity-toggle"
        onClick={toggleOpen}
        className="w-full flex items-center gap-2 px-3 py-2 transition-colors hover:opacity-80"
        style={{ color: 'var(--pd-text)' }}
      >
        {open ? <ChevronDown className="w-3.5 h-3.5" /> : <ChevronRight className="w-3.5 h-3.5" />}
        <History className="w-3.5 h-3.5" style={{ color: 'var(--pd-subtle)' }} />
        <span className="text-[11px] uppercase tracking-[0.08em] font-semibold" style={{ color: 'var(--pd-subtle)' }}>
          History
        </span>
        {loaded ? (
          <span data-testid="entity-activity-count" className="text-[10px] tabular-nums ml-auto" style={{ color: 'var(--pd-subtle)' }}>
            {logs.length}
          </span>
        ) : null}
      </button>
      {open && (
        <div className="px-3 pb-3">
          {loading && !loaded ? (
            <div className="text-[11px] py-2" style={{ color: 'var(--pd-subtle)' }}>Loading history…</div>
          ) : logs.length === 0 ? (
            <div data-testid="entity-activity-empty" className="text-[11px] py-2" style={{ color: 'var(--pd-subtle)' }}>
              No activity recorded yet.
            </div>
          ) : (
            <ol className="space-y-1.5" data-testid="entity-activity-list">
              {logs.map((log) => {
                const label = ACTION_LABEL[log.action] || log.action;
                const meta = log.metadata && typeof log.metadata === 'object' ? log.metadata : null;
                const elapsed = meta && typeof meta.elapsedMinutes === 'number' ? `${meta.elapsedMinutes}m` : null;
                return (
                  <li
                    key={log.id}
                    data-testid={`entity-activity-row-${log.id}`}
                    className="flex items-baseline gap-2 text-[11px]"
                    style={{ color: 'var(--pd-text)' }}
                  >
                    <span className="font-medium">{label}</span>
                    {elapsed ? (
                      <span className="tabular-nums" style={{ color: 'var(--pd-subtle)' }}>+{elapsed}</span>
                    ) : null}
                    <span className="ml-auto tabular-nums text-[10px]" style={{ color: 'var(--pd-subtle)' }}>
                      {relativeTime(log.timestamp)}
                    </span>
                  </li>
                );
              })}
            </ol>
          )}
        </div>
      )}
    </div>
  );
}
